import {
  type CellData,
  type Column,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
  assignPrototypeAPIs,
  assignTableAPIs,
  functionalUpdate,
} from '@tanstack/react-table';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type {
  DataTableColumnFontStyleProps,
  DataTableColumnFontStyleState,
} from './column-font-style-types.js';

/**
 * Configuration hook for the DataTable FontStyle feature.
 * @internal
 */
export function useColumnFontStyle<TData extends DataTableRowData>(
  props: DataTableColumnFontStyleProps,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const configFontStyle = useDataTableConfigElement('fontStyle');

  const [columnFontStyle, onColumnFontStyleChange] = useControllableState({
    value: props.fontStyle ?? configFontStyle,
    defaultValue: props.defaultFontStyle,
    onChange: props.onFontStyleChange,
  });

  // oxlint-disable-next-line react/immutability
  options.state = {
    ...options.state,
    columnFontStyle,
  };
  // oxlint-disable-next-line react/immutability
  options.onColumnFontStyleChange = onColumnFontStyleChange;
}

/**
 * Helper function that will allow access to the columnFontStyle state of an individual column.
 */
function table_setColumnFontStyle<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  updater: Updater<DataTableColumnFontStyleState>,
): void {
  const newValue = functionalUpdate(
    updater,
    table.atoms.columnFontStyle?.get() ?? {},
  );
  table.options.onColumnFontStyleChange?.(newValue);
}

// Add a toggleFontStyle function onto the column so that it can be accessed
// during rendering.
function column_toggleFontStyle<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  value?: 'code' | 'text',
): void {
  table_setColumnFontStyle(column.table, (old) => {
    let newValue = value;
    // If there is no passed value, apply the toggle logic
    if (newValue === undefined) {
      if (old[column.id] !== undefined) {
        newValue = old[column.id] === 'code' ? 'text' : 'code';
      } else {
        newValue =
          column.table.atoms.visualVariantConfig?.get().fontStyle === 'code'
            ? 'text'
            : 'code';
      }
    }
    return {
      ...old,
      [column.id]: newValue,
    };
  });
}

/**
 * Helper function that will allow access to the columnFontStyle state of an individual column.
 * @returns
 */
function column_getFontStyle<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
): 'code' | 'text' | undefined {
  const fontStyleState = column.table.atoms.columnFontStyle?.get();

  // Check if a font style is defined in the state
  if (fontStyleState?.[column.id]) {
    return fontStyleState?.[column.id];
  }
  // Check if some font style is defined in the columnDef meta based on column types
  return (
    column.columnDef.meta?.fontStyle ??
    column.table.atoms.visualVariantConfig.get().fontStyle
  );
}

export function constructColumnFontStyleFeature(): TableFeature {
  return {
    getInitialState: (initialState) => {
      return {
        columnFontStyle: structuredClone({}),
        ...initialState,
      };
    },
    assignColumnPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs(
          'DataTableColumnFontStyleFeature',
          prototype,
          table,
          {
            column_getFontStyle: {
              fn: (column) => column_getFontStyle(column),
            },
            column_toggleFontStyle: {
              fn: (column, value) => column_toggleFontStyle(column, value),
            },
          },
        );
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableColumnFontStyleFeature', table, {
          table_setColumnFontStyle: {
            fn: (updater) => table_setColumnFontStyle(table, updater),
          },
        });
      },
    ),
  };
}

/**
 * Feature implementation for FontStyle. This contains the logic that will be given
 * to the tanstack table. The implementing interface is important as these are
 * the function that will be called by the tanstack table core during render.
 * @internal
 */
export const DataTableColumnFontStyleFeature =
  constructColumnFontStyleFeature();
